// =============================================================================
// DeliveryDatePicker — region-aware calendar with delivery + production caps
// =============================================================================
// Replaces a plain <Input type="date"> wherever the sales girl needs to pick
// a delivery date. Shows which days are eligible for the customer's region,
// greys out fully-booked days, and surfaces production load for the natural
// production day (delivery − leadDays).

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { Region } from '@kalachand/shared';
import { apiGet } from '../lib/api';
import { cn } from '../lib/cn';
import { businessTodayIso, addDaysIso } from '../lib/dates';
import { Button } from './Button';
import { IconButton } from './IconButton';

interface DeliveryDayStatus {
  date: string;
  eligible: boolean;
  deliveriesScheduled: number;
  deliveryCap: number;
  atCapacity: boolean;
  productionDate: string | null;
  productionScheduled: number;
  productionCap: number;
}

interface SchedulingConfig {
  regionsByDayOfWeek: Record<string, Region[]>;
  productionPerDay: number;
  deliveriesPerDay: number;
  productionLeadDays: number;
}

const REGION_LABELS: Record<Region, string> = {
  center: 'Center',
  north: 'North',
  east: 'East',
  south: 'South',
  west: 'West',
};
const REGION_OPTIONS: Region[] = ['center', 'north', 'east', 'south', 'west'];

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function isoDayUTC(d: Date): string {
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Build a 6-week grid (UTC) starting on the Monday at or before the month's
 * 1st. All date math is UTC so cell isos line up with the backend + earliestDate
 * (the app stores delivery dates as UTC days; mixing local + UTC shifted cells
 * by a day in UTC+ timezones like Mauritius).
 */
function buildGrid(monthFirstUTC: Date): { date: Date; iso: string }[] {
  const offset = (monthFirstUTC.getUTCDay() + 6) % 7; // days back to Monday
  const monday = new Date(monthFirstUTC);
  monday.setUTCDate(monthFirstUTC.getUTCDate() - offset);
  const cells: { date: Date; iso: string }[] = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(monday);
    d.setUTCDate(monday.getUTCDate() + i);
    cells.push({ date: d, iso: isoDayUTC(d) });
  }
  return cells;
}

interface DeliveryDatePickerProps {
  /** Customer's region. Pass null when not yet set — the picker shows a region selector first. */
  region: Region | null;
  /** Called when the sales girl picks (or clears) the region. */
  onRegionChange: (r: Region) => void;
  /** Selected delivery date (ISO YYYY-MM-DD), or empty string. */
  value: string;
  onChange: (iso: string) => void;
  /** If true, customer has no region yet — show the "Set region first" prompt. */
  customerNeedsRegion?: boolean;
  /** Label for the customer (used in the "Set region for X" hint). */
  customerLabel?: string;
  /** Earliest selectable date (ISO) — from the order's goods' lead times. */
  earliestDate?: string;
  /**
   * Fast Track (priority) order — the customer paid to push both manufacturing
   * and delivery through, so off-route days and fully-booked days are still
   * selectable. Only past / too-soon days stay disabled.
   */
  priority?: boolean;
}

export function DeliveryDatePicker({
  region,
  onRegionChange,
  value,
  onChange,
  customerNeedsRegion,
  customerLabel,
  earliestDate,
  priority,
}: DeliveryDatePickerProps): JSX.Element {
  const [monthAnchor, setMonthAnchor] = useState(() => {
    const base = earliestDate ? new Date(earliestDate) : new Date();
    return new Date(Date.UTC(base.getUTCFullYear(), base.getUTCMonth(), 1));
  });

  // Need a region to query availability — otherwise show the prompt only.
  const availability = useQuery({
    queryKey: ['delivery-availability', region, monthAnchor.toISOString()],
    queryFn: () =>
      apiGet<DeliveryDayStatus[]>('/scheduling/delivery-availability', {
        region: region!,
        from: isoDayUTC(monthAnchor),
        days: 42,
      }),
    enabled: !!region,
  });

  const config = useQuery({
    queryKey: ['scheduling-config'],
    queryFn: () => apiGet<SchedulingConfig>('/scheduling/config'),
  });

  const grid = useMemo(() => buildGrid(monthAnchor), [monthAnchor]);
  const byDate = useMemo(() => {
    const map: Record<string, DeliveryDayStatus> = {};
    for (const d of availability.data ?? []) map[d.date] = d;
    return map;
  }, [availability.data]);

  const monthLabel = monthAnchor.toLocaleDateString('en-MU', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
  const todayIso = businessTodayIso();
  // A delivery can never be booked for today — the floor is always tomorrow,
  // pushed later by the goods' lead time (earliestDate) when that's stricter.
  const minDeliveryIso = addDaysIso(todayIso, 1);
  const earliestSelectable =
    earliestDate && earliestDate > minDeliveryIso ? earliestDate : minDeliveryIso;

  return (
    <div className="space-y-3">
      {/* Region row */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        {region ? (
          <div className="flex items-center gap-2 text-sm">
            <span className="text-ink-3">Routing to</span>
            <span className="px-2 py-0.5 rounded-full bg-accent-soft text-accent text-xs font-medium uppercase tracking-wider">
              {REGION_LABELS[region]}
            </span>
            <Button variant="ghost" size="xs" onClick={() => onRegionChange(region)}>
              Change
            </Button>
          </div>
        ) : null}
        {value && (
          <Button variant="tertiary-danger" size="xs" onClick={() => onChange('')}>
            Clear date
          </Button>
        )}
      </div>

      {/* Earliest-delivery hint — driven by the order's goods' lead times */}
      {region && priority ? (
        <div className="text-[11px] text-accent bg-accent-soft border border-accent/30 rounded px-2.5 py-1.5">
          ⚡ Fast Track — manufacturing and delivery are pushed through. Any day from{' '}
          <span className="font-medium">{earliestSelectable}</span> is selectable, including
          off-route and fully-booked days.
        </div>
      ) : (
        region && earliestDate && earliestDate > todayIso && (
          <div className="text-[11px] text-ink-2 bg-paper-2 border border-rule-2 rounded px-2.5 py-1.5">
            ⏱ Earliest delivery for this order:{' '}
            <span className="font-medium text-ink">{earliestDate}</span> — earlier days are greyed
            out (lead time &amp; factory production capacity).
          </div>
        )
      )}

      {/* Region selector — required first if missing */}
      {!region && (
        <div className="rounded-md border border-warning/30 bg-warning-soft/40 p-4">
          <div className="text-xs uppercase tracking-[0.18em] text-warning mb-1">Region needed</div>
          <p className="text-sm text-ink mb-3">
            Pick a delivery region for{' '}
            <span className="font-medium">{customerLabel ?? 'this customer'}</span> so the calendar
            can show the right truck days.{' '}
            {customerNeedsRegion && (
              <span className="text-ink-3 text-xs block mt-1">
                This will save to the customer record too.
              </span>
            )}
          </p>
          <select
            value=""
            onChange={(e) => onRegionChange(e.target.value as Region)}
            className="kc-input w-full"
            autoFocus
          >
            <option value="">— pick a region —</option>
            {REGION_OPTIONS.map((r) => (
              <option key={r} value={r}>
                {REGION_LABELS[r]}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Month header + grid */}
      {region && (
        <div className="rounded-md border border-rule bg-surface">
          <div className="flex items-center justify-between px-3 py-2 border-b border-rule-2">
            <IconButton
              aria-label="Previous month"
              size="sm"
              onClick={() =>
                setMonthAnchor(
                  new Date(
                    Date.UTC(monthAnchor.getUTCFullYear(), monthAnchor.getUTCMonth() - 1, 1),
                  ),
                )
              }
            >
              ‹
            </IconButton>
            <div className="text-sm font-medium text-ink">{monthLabel}</div>
            <IconButton
              aria-label="Next month"
              size="sm"
              onClick={() =>
                setMonthAnchor(
                  new Date(
                    Date.UTC(monthAnchor.getUTCFullYear(), monthAnchor.getUTCMonth() + 1, 1),
                  ),
                )
              }
            >
              ›
            </IconButton>
          </div>
          <div className="grid grid-cols-7 border-b border-rule-2 bg-paper-2/40">
            {WEEKDAYS.map((w) => (
              <div
                key={w}
                className="text-[10px] uppercase tracking-[0.18em] text-ink-3 text-center py-1.5 font-medium"
              >
                {w}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7">
            {grid.map((cell, idx) => {
              const status = byDate[cell.iso];
              const rightEdge = (idx + 1) % 7 === 0;
              const bottomEdge = idx >= 35;
              const isPast = cell.iso < todayIso;
              const tooSoon = cell.iso < earliestSelectable;
              const isToday = cell.iso === todayIso;
              const isSelected = cell.iso === value;
              const inMonth = cell.date.getUTCMonth() === monthAnchor.getUTCMonth();

              // Disabled reasons (precedence: past → too-soon → out-of-month → ineligible → at-capacity)
              const eligible = status?.eligible ?? false;
              const atCap = status?.atCapacity ?? false;
              const productionFull =
                (status?.productionScheduled ?? 0) >= (status?.productionCap ?? Infinity);
              // Fast Track (priority) pushes through off-route + fully-booked days,
              // so only past / too-soon / out-of-month days stay unselectable.
              const disabled = priority
                ? isPast || tooSoon || !inMonth
                : isPast || tooSoon || !inMonth || !status || !eligible || atCap;

              // Every in-month day the sales person can't pick is dimmed like a
              // past day (faded) so unavailability reads at a glance — whether
              // it's past, before the goods' lead time (too soon), or off-route.
              // The one exception is a route day that's merely full: it keeps a
              // red "Full" marker so the reason stays distinct. On Fast Track an
              // off-route / full day stays fully selectable (not faded).
              const full = inMonth && eligible && atCap && !isPast && !tooSoon;
              const faded =
                inMonth &&
                !isSelected &&
                !full &&
                (isPast || tooSoon || (!priority && (!eligible || !status)));

              const deliveryUsage = status
                ? `${status.deliveriesScheduled}/${status.deliveryCap}`
                : '';
              const productionUsage = status?.productionDate
                ? `${status.productionScheduled}/${status.productionCap}`
                : '';

              return (
                <button
                  key={cell.iso}
                  type="button"
                  onClick={() => !disabled && onChange(cell.iso)}
                  disabled={disabled}
                  className={cn(
                    'relative text-left p-2 min-h-[74px] transition focus:outline-none',
                    !rightEdge && 'border-r border-rule-2',
                    !bottomEdge && 'border-b border-rule-2',
                    !inMonth && 'bg-paper-2/30',
                    inMonth && !disabled && 'hover:bg-paper-2 cursor-pointer',
                    full && 'bg-negative-soft/30',
                    full && disabled && 'cursor-not-allowed',
                    faded && 'opacity-40 cursor-not-allowed',
                    isSelected && 'ring-2 ring-inset ring-accent bg-accent-soft',
                  )}
                  title={
                    isPast
                      ? 'Past date'
                      : tooSoon
                        ? `Too soon — earliest delivery ${earliestSelectable}`
                        : !inMonth
                          ? ''
                          : !eligible
                            ? `Not on ${REGION_LABELS[region]} rotation${priority ? ' — Fast Track override' : ''}`
                            : atCap
                              ? `Day is fully booked (${deliveryUsage})${priority ? ' — Fast Track override' : ''}`
                              : `${deliveryUsage} delivery · ${productionUsage} production`
                  }
                >
                  <div className="flex items-baseline justify-between">
                    <span
                      className={cn(
                        'text-sm leading-none kc-serif',
                        !inMonth
                          ? 'text-ink-3/40'
                          : isToday
                            ? 'text-accent font-medium'
                            : 'text-ink',
                      )}
                    >
                      {cell.date.getUTCDate()}
                    </span>
                    {isToday && inMonth && (
                      <span className="text-[8px] uppercase tracking-[0.18em] text-accent">
                        today
                      </span>
                    )}
                  </div>
                  {full && (
                    <div className="mt-1 inline-block text-[8px] uppercase tracking-wider font-bold text-white bg-negative rounded px-1 py-0.5">
                      Full
                    </div>
                  )}
                  {inMonth && status && eligible && (
                    <div className="mt-1.5 space-y-0.5">
                      <div
                        className={cn(
                          'text-[10px] flex items-center gap-1',
                          atCap ? 'text-negative font-medium' : 'text-ink-2',
                        )}
                      >
                        <span
                          className={cn(
                            'inline-block w-1.5 h-1.5 rounded-full',
                            atCap ? 'bg-negative' : 'bg-accent',
                          )}
                        />
                        {deliveryUsage} D
                      </div>
                      {status.productionDate && (
                        <div
                          className={cn(
                            'text-[10px] flex items-center gap-1',
                            productionFull ? 'text-warning' : 'text-ink-3',
                          )}
                        >
                          <span
                            className={cn(
                              'inline-block w-1.5 h-1.5 rounded-full',
                              productionFull ? 'bg-warning' : 'bg-ink-3/50',
                            )}
                          />
                          {productionUsage} P
                        </div>
                      )}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
          {availability.isLoading && (
            <div className="px-3 py-2 text-[11px] text-ink-3 italic">Loading availability…</div>
          )}
          {config.data && (
            <div className="px-3 py-2 text-[10px] text-ink-3 border-t border-rule-2 flex items-center gap-4">
              <span>
                <b className="text-ink-2">D</b> = delivery slots (cap {config.data.deliveriesPerDay}
                )
              </span>
              <span>
                <b className="text-ink-2">P</b> = production load on day −{' '}
                {config.data.productionLeadDays} (cap {config.data.productionPerDay})
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
